import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {
    ClientSelectionDialogComponent,
    ClientSelectionDialogData
} from '../../components/client-selection-dialog/client-selection-dialog.component';
import {SelectableEntity} from '../../services/auth/entity-selection-dialog.service';

@Injectable({
    providedIn: 'root'
})
export class ClientSelectionDialogService {
    private dialog = inject(MatDialog);

    private isDialogOpen = false;
    private currentDialogRef: any = null;

    /**
     * Opens a dialog for selecting an entity (client or account)
     * @param entities The list of entities to choose from
     * @param title Optional title for the dialog
     * @param message Optional message for the dialog
     * @param entityType The type of entity being selected ('client' or 'account')
     * @returns An Observable that resolves to the selected entity ID or null if cancelled
     */
    selectEntity(
        entities: SelectableEntity[], 
        title?: string, 
        message?: string,
        entityType: 'client' | 'account' = 'client',
        refreshEntities?: () => Promise<SelectableEntity[]> | SelectableEntity[]
    ): Observable<string | null> {
        // If a dialog is already open, return the existing dialog's afterClosed Observable
        if (this.isDialogOpen && this.currentDialogRef) {
            console.log(`${entityType} selection dialog is already open, reusing existing dialog`);
            return this.currentDialogRef.afterClosed();
        }

        console.log(`Opening ${entityType} selection dialog`);
        this.isDialogOpen = true;
        this.currentDialogRef = this.dialog.open(ClientSelectionDialogComponent, {
            width: '500px',
            disableClose: true, // Prevent closing by clicking outside
            data: {
                entities,
                title,
                message,
                entityType,
                refreshEntities
            } as ClientSelectionDialogData
        });

        // Reset the flag when the dialog is closed
        this.currentDialogRef.afterClosed().subscribe(() => {
            console.log(`${entityType} selection dialog closed`);
            this.isDialogOpen = false;
            this.currentDialogRef = null;
        });

        return this.currentDialogRef.afterClosed();
    }

    /**
     * Opens a dialog for selecting a client (convenience method)
     * @param clients The list of clients to choose from
     * @param title Optional title for the dialog
     * @param message Optional message for the dialog
     * @returns An Observable that resolves to the selected client ID or null if cancelled
     */
    selectClient(
        clients: SelectableEntity[],
        title?: string,
        message?: string,
        refreshClients?: () => Promise<SelectableEntity[]> | SelectableEntity[]
    ): Observable<string | null> {
        return this.selectEntity(clients, title, message, 'client', refreshClients);
    }
}
